import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent } from '@/components/ui/card'
import { BillingsMatrixTable } from '@/components/sales-trackers/BillingsMatrix'
import { FiscalYearPicker } from '@/components/sales-trackers/FiscalYearPicker'
import { useBillingsFiscalYears, useBillingsProducts } from '@/hooks/use-api'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { explainError } from '@/lib/explain-error'
import { useBillingsPeriod, periodQuery, periodLabel } from '@/hooks/use-billings-period'
import { NoBillingsYet } from '@/components/sales-trackers/no-billings-yet'
import { isNoBillingsYet } from '@/components/sales-trackers/billings-empty'

/**
 * Billings by product category.
 *
 * Groups come from billings.account_categories rather than being hardcoded, so a
 * newly categorised sales account appears here without a deploy. Two of the
 * spreadsheet's own row labels were wrong and are corrected in that seed data: its
 * "Grackle Workspace" row also carried Sales-CA/US/International and Grackle
 * Office, and its "WebKey IT" row was actually Intercompany revenue.
 */
export default function SalesTrackersProducts() {
  useDocumentTitle('Billings by product')

  const years = useBillingsFiscalYears()
  const period = useBillingsPeriod(years.data)
  const { fy, granularity, month, quarter, setPeriod } = period
  const products = useBillingsProducts({ fy, ...periodQuery(period) })

  const shownPeriod = periodLabel(granularity, {
    fy: fy ?? products.data?.fiscal_year,
    month,
    quarter,
    monthLabel: products.data?.months?.[0]?.label,
  })

  return (
    <section className="p-6 space-y-6" aria-labelledby="sales-trackers-heading">
      <div>
        <h1 id="sales-trackers-heading" className="text-2xl font-semibold">
          Billings by product
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Net billings grouped by QuickBooks sales account.
        </p>
      </div>

      {years.data && years.data.length > 0 && (
        <FiscalYearPicker
          years={years.data}
          value={fy ?? products.data?.fiscal_year}
          onChange={(next) => setPeriod({ fy: next })}
          period={{ granularity, month, quarter, onChange: setPeriod }}
        />
      )}

      <p role="status" aria-live="polite" className="sr-only">
        {products.isFetching ? 'Loading product figures' : products.data ? 'Loaded' : ''}
      </p>

      {products.isError &&
        (isNoBillingsYet(products.error) ? (
          <NoBillingsYet what="The product breakdown" />
        ) : (
          <Alert variant="destructive">
            <AlertTitle>Could not load the product breakdown</AlertTitle>
            <AlertDescription>
              {explainError(products.error, 'Something went wrong loading the product figures.')}
            </AlertDescription>
          </Alert>
        ))}

      {products.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {products.data && (
        <Card>
          <CardContent className="pt-6">
            <BillingsMatrixTable
              // Drill-down: the lines behind a product group, for the period on screen.
              // `period.search` carries the whole period rather than just the year, which
              // is what stops the link quietly widening a month back to a year.
              linkFor={(row) =>
                `/sales-trackers/transactions?${period.search}${
                  period.search ? '&' : ''
                }product_group=${encodeURIComponent(row.label)}`
              }
              matrix={products.data}
              dimensionLabel="Product"
              caption={`Billings by product category for ${shownPeriod}, by month`}
            />
          </CardContent>
        </Card>
      )}
    </section>
  )
}
